"use client";

import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";

import { DeskBackButton } from "@/components/DeskBackButton";
import { DashboardInsightHub } from "@/components/dashboard/DashboardInsightHub";
import { ExpiryDeskWordmark } from "@/components/ExpiryDeskWordmark";
import UpgradePrompt from "@/components/upgrade/UpgradePrompt";
import { usePlanGate } from "@/hooks/usePlanGate";
import { resolveExpiryDate } from "@/lib/resolveExpiryDate";
import { normalizeRuntimePlan, PLANS } from "@/lib/plans/planConfig";
import { formatZarAmount, OWNER_MONTHLY_ZAR } from "@/lib/pricingDisplay";
import { supabase } from "@/lib/supabaseClient";

const INVENTORY_CATEGORY_IDS = ["fridge", "schedule6", "schedule35", "otc"] as const;
const INVENTORY_CATEGORY_LABELS: Record<(typeof INVENTORY_CATEGORY_IDS)[number], { title: string; icon: string }> = {
  fridge: { title: "Fridge", icon: "❄️" },
  schedule6: { title: "Schedule 6", icon: "🔒" },
  schedule35: { title: "Schedule 3–5", icon: "💊" },
  otc: { title: "OTC 1–2", icon: "🏪" },
};

function normalizeCategory(raw: unknown): (typeof INVENTORY_CATEGORY_IDS)[number] | null {
  const s = typeof raw === "string" ? raw.trim() : "";
  return INVENTORY_CATEGORY_IDS.includes(s as (typeof INVENTORY_CATEGORY_IDS)[number]) ? (s as (typeof INVENTORY_CATEGORY_IDS)[number]) : null;
}

type ExpiryTierKey = "overdue" | "critical" | "warning" | "safe";

function expiryTierForMed(m: {
  status?: string | null;
  expiryMonth: number;
  expiryYear: number;
}): ExpiryTierKey | null {
  if (m.status) return null;
  const exp = resolveExpiryDate(Number(m.expiryMonth), Number(m.expiryYear), "last");
  const days = Math.ceil((exp.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0) return "overdue";
  if (days <= 30) return "critical";
  if (days <= 90) return "warning";
  return "safe";
}

const TIER_META: Record<
  ExpiryTierKey,
  { label: string; short: string; border: string; tint: string }
> = {
  overdue: {
    label: "Overdue",
    short: "Past expiry",
    border: "var(--ed-ui-critical-strong)",
    tint: "var(--ed-ui-critical-bg)",
  },
  critical: {
    label: "Critical (≤30d)",
    short: "≤30 days",
    border: "var(--ed-ui-critical)",
    tint: "rgba(254, 243, 199, 0.45)",
  },
  warning: {
    label: "At risk (31–90d)",
    short: "31–90 days",
    border: "var(--ed-ui-warning-strong)",
    tint: "var(--ed-ui-warning-bg)",
  },
  safe: {
    label: "Safe (>90d)",
    short: ">90 days",
    border: "var(--ed-ui-safe-strong)",
    tint: "var(--ed-ui-safe-bg)",
  },
};

const CLASSIFY_UNDO_MS = 10_000;

/**
 * In-app Insights: inventory intelligence from desk data. Owner tier adds richer views + digest snippets.
 */
export function InsightsDeskPage() {
  const [plan, setPlan] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [meds, setMeds] = useState<Array<{
    id: string | number;
    name?: string;
    quantity: number;
    expiryMonth: number;
    expiryYear: number;
    status?: string | null;
    pricePerBox?: number | null;
    supplierName?: string | null;
    dosage?: string | null;
    unit?: string | null;
    productCategory?: string | null;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [emailAddr, setEmailAddr] = useState("");
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [showExtendedSections, setShowExtendedSections] = useState(false);

  const isOwner = plan === "premium";
  const runtimePlan = normalizeRuntimePlan(plan || "free", Boolean(userId));
  const { canUse } = usePlanGate();
  const [selectedUnclassifiedIds, setSelectedUnclassifiedIds] = useState<Set<string>>(() => new Set());
  const [classifyBusy, setClassifyBusy] = useState(false);
  const [classifyError, setClassifyError] = useState<string | null>(null);
  const [dropHoverCategory, setDropHoverCategory] = useState<(typeof INVENTORY_CATEGORY_IDS)[number] | null>(null);

  const unclassifiedMeds = useMemo(
    () => meds.filter((m) => !normalizeCategory(m.productCategory)),
    [meds],
  );

  const inventorySectionAgg = useMemo(() => {
    const emptyTier = () => ({ total: 0, overdue: 0, critical: 0, warning: 0, safe: 0 });
    const agg: Record<string, ReturnType<typeof emptyTier>> = {
      fridge: emptyTier(),
      schedule6: emptyTier(),
      schedule35: emptyTier(),
      otc: emptyTier(),
      unassigned: emptyTier(),
    };
    const now = new Date();
    meds.forEach((m) => {
      if (m.status) return;
      const exp = resolveExpiryDate(Number(m.expiryMonth), Number(m.expiryYear), "last");
      const days = Math.ceil((exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      const cat = normalizeCategory(m.productCategory) || "unassigned";
      const bucket = agg[cat];
      bucket.total += 1;
      if (days < 0) bucket.overdue += 1;
      else if (days <= 30) bucket.critical += 1;
      else if (days <= 90) bucket.warning += 1;
      else bucket.safe += 1;
    });
    return agg;
  }, [meds]);

  const medsByCategoryTier = useMemo(() => {
    type M = (typeof meds)[number];
    const empty = (): Record<ExpiryTierKey, M[]> => ({
      overdue: [],
      critical: [],
      warning: [],
      safe: [],
    });
    const out = {
      fridge: empty(),
      schedule6: empty(),
      schedule35: empty(),
      otc: empty(),
    };
    meds.forEach((m) => {
      const cat = normalizeCategory(m.productCategory);
      if (!cat) return;
      const tier = expiryTierForMed(m);
      if (!tier) return;
      out[cat][tier].push(m);
    });
    return out;
  }, [meds]);

  const [expandedCategory, setExpandedCategory] = useState<(typeof INVENTORY_CATEGORY_IDS)[number] | null>(null);
  const [classifyUndo, setClassifyUndo] = useState<{
    snapshot: typeof meds;
    ids: string[];
    categoryTitle: string;
  } | null>(null);
  const classifyUndoTimerRef = useRef<number | null>(null);

  const clearClassifyUndoTimer = useCallback(() => {
    if (classifyUndoTimerRef.current != null) {
      window.clearTimeout(classifyUndoTimerRef.current);
      classifyUndoTimerRef.current = null;
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (!supabase) {
        setPlan(null);
        return;
      }
      const { data: s } = await supabase.auth.getSession();
      const tok = s.session?.access_token;
      const email = s.session?.user?.email ?? "";
      const uid = s.session?.user?.id ?? null;
      setUserId(uid);
      setEmailAddr((prev) => (prev.trim() ? prev : email));
      if (!tok) {
        setPlan(null);
        setMeds([]);
        return;
      }
      const res = await fetch("/api/billing/status", { headers: { Authorization: `Bearer ${tok}` } });
      const data = await res.json();
      if (res.ok && data?.plan) setPlan(String(data.plan).toLowerCase());
      else setPlan("free");

      const { data: row } = uid
        ? await supabase.from("email_subscriptions").select("email,enabled,verified").eq("user_id", uid).maybeSingle()
        : { data: null };
      if (row?.email) setEmailAddr(row.email);
      setSubscribed(Boolean(row?.enabled && row?.verified));

      if (uid) {
        const { data: medRows } = await supabase
          .from("medications")
          .select("id,name,dosage,quantity,unit,expiry_month,expiry_year,status,price_per_box,supplier_name,product_category,deleted_at")
          .eq("user_id", uid)
          .is("deleted_at", null);
        const mapped = (medRows || [])
          .filter((m) => Number(m.expiry_month) > 0 && Number(m.expiry_year) > 0)
          .map((m) => ({
            id: m.id,
            name: m.name || "",
            quantity: Number(m.quantity) || 0,
            expiryMonth: Number(m.expiry_month),
            expiryYear: Number(m.expiry_year),
            status: m.status || null,
            pricePerBox: m.price_per_box != null ? Number(m.price_per_box) : null,
            supplierName: m.supplier_name || null,
            dosage: m.dosage || null,
            unit: m.unit || null,
            productCategory: m.product_category || null,
          }));
        setMeds(mapped);
      } else {
        setMeds([]);
      }
    } catch {
      setPlan("pro");
      setMeds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const allowed = new Set(unclassifiedMeds.map((m) => String(m.id)));
    setSelectedUnclassifiedIds((prev) => {
      const next = new Set<string>();
      let pruned = false;
      prev.forEach((id) => {
        if (allowed.has(id)) next.add(id);
        else pruned = true;
      });
      if (!pruned && next.size === prev.size) return prev;
      return next;
    });
  }, [unclassifiedMeds]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSelectedUnclassifiedIds(new Set());
        setExpandedCategory(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const suppressCardClickRef = useRef(false);

  const applyProductCategory = useCallback(
    async (rawIds: string[], category: (typeof INVENTORY_CATEGORY_IDS)[number]) => {
      const ids = [...new Set(rawIds.map(String))].filter(Boolean);
      if (!ids.length || !supabase || !userId) return;
      clearClassifyUndoTimer();
      setClassifyUndo(null);
      setClassifyBusy(true);
      setClassifyError(null);
      let snapshot: typeof meds = [];
      setMeds((cur) => {
        snapshot = cur;
        return cur.map((m) => (ids.includes(String(m.id)) ? { ...m, productCategory: category } : m));
      });
      setSelectedUnclassifiedIds((sel) => {
        const n = new Set(sel);
        ids.forEach((id) => n.delete(id));
        return n;
      });
      try {
        const { error } = await supabase
          .from("medications")
          .update({ product_category: category })
          .eq("user_id", userId)
          .in("id", ids);
        if (error) {
          setMeds(snapshot);
          setClassifyError(error.message || "Could not update category.");
        } else {
          clearClassifyUndoTimer();
          const categoryTitle = INVENTORY_CATEGORY_LABELS[category].title;
          setClassifyUndo({ snapshot, ids, categoryTitle });
          classifyUndoTimerRef.current = window.setTimeout(() => {
            classifyUndoTimerRef.current = null;
            setClassifyUndo(null);
          }, CLASSIFY_UNDO_MS);
        }
      } catch {
        setMeds(snapshot);
        setClassifyError("Could not update category.");
      } finally {
        setClassifyBusy(false);
      }
    },
    [userId, clearClassifyUndoTimer],
  );

  const undoClassify = useCallback(async () => {
    const u = classifyUndo;
    if (!u || !supabase || !userId) return;
    const client = supabase;
    clearClassifyUndoTimer();
    setClassifyUndo(null);
    setMeds(u.snapshot);
    try {
      const results = await Promise.all(
        u.ids.map((id) => {
          const prev = u.snapshot.find((m) => String(m.id) === id);
          const raw = prev?.productCategory;
          const pc =
            raw != null && String(raw).trim() && normalizeCategory(raw) ? String(raw).trim() : null;
          return client.from("medications").update({ product_category: pc }).eq("user_id", userId).eq("id", id);
        }),
      );
      if (results.some((r) => r.error)) void load();
    } catch {
      void load();
    }
  }, [classifyUndo, supabase, userId, clearClassifyUndoTimer, load]);

  useEffect(() => {
    return () => {
      clearClassifyUndoTimer();
    };
  }, [clearClassifyUndoTimer]);

  useEffect(() => {
    if (!showExtendedSections) return;
    const t = window.setTimeout(() => {
      document.getElementById("insights-extended")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => window.clearTimeout(t);
  }, [showExtendedSections]);

  const parseDragPayload = (e: DragEvent): string[] | null => {
    try {
      const raw = e.dataTransfer.getData("application/json");
      if (!raw) return null;
      const o = JSON.parse(raw) as { ids?: unknown };
      if (!Array.isArray(o.ids)) return null;
      return o.ids.filter((x): x is string => typeof x === "string" && x.length > 0);
    } catch {
      return null;
    }
  };

  const onCardDragStart = (e: DragEvent, id: string) => {
    const idStr = String(id);
    const ids =
      selectedUnclassifiedIds.has(idStr) && selectedUnclassifiedIds.size > 0
        ? Array.from(selectedUnclassifiedIds)
        : [idStr];
    e.dataTransfer.setData("application/json", JSON.stringify({ ids }));
    e.dataTransfer.effectAllowed = "move";
  };

  const onCardDragEnd = () => {
    suppressCardClickRef.current = true;
    window.setTimeout(() => {
      suppressCardClickRef.current = false;
    }, 0);
  };

  const onCategoryDragOver = (e: DragEvent, cid: (typeof INVENTORY_CATEGORY_IDS)[number]) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropHoverCategory(cid);
  };

  const onCategoryDrop = (e: DragEvent, cid: (typeof INVENTORY_CATEGORY_IDS)[number]) => {
    e.preventDefault();
    setDropHoverCategory(null);
    const ids = parseDragPayload(e);
    if (!ids?.length || classifyBusy) return;
    void applyProductCategory(ids, cid);
  };

  const toggleCardSelect = (id: string, multi: boolean) => {
    const idStr = String(id);
    setSelectedUnclassifiedIds((prev) => {
      if (multi) {
        const n = new Set(prev);
        if (n.has(idStr)) n.delete(idStr);
        else n.add(idStr);
        return n;
      }
      return new Set([idStr]);
    });
  };

  useEffect(() => {
    const onEnd = () => setDropHoverCategory(null);
    window.addEventListener("dragend", onEnd);
    return () => window.removeEventListener("dragend", onEnd);
  }, []);

  const subscribeNewsletter = async () => {
    setSubError("");
    if (!supabase) {
      setSubError("Sign in to subscribe.");
      return;
    }
    const email = emailAddr.trim();
    if (!email) {
      setSubError("Enter your email.");
      return;
    }
    setSubLoading(true);
    try {
      const { data: s } = await supabase.auth.getSession();
      const tok = s.session?.access_token;
      if (!tok) {
        setSubError("Session expired. Sign in again.");
        return;
      }
      const response = await fetch("/api/email-reminders/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tok}`,
        },
        body: JSON.stringify({ subscriberEmail: email }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        setSubError("We couldn't set up your reminders. Please try again.");
        return;
      }
      setSubscribed(true);
    } catch {
      setSubError("Could not subscribe.");
    } finally {
      setSubLoading(false);
    }
  };

  return (
    <div className="ed-insights-shell">
      <header className="ed-insights-topbar" aria-label="Insights navigation">
        <div className="ed-insights-topbar__left">
          <DeskBackButton />
          <ExpiryDeskWordmark
            variant="nav"
            showTagline={false}
            href="/dashboard"
            className="ed-insights-topbar__logo"
            linkClassName="ed-desk-brand-link"
          />
        </div>
        <div className="ed-insights-topbar__aux">
          <Link href="/dashboard">Desk</Link>
        </div>
      </header>

      <main className="ed-insights-main">
        <div className="ed-insights-hero">
          <h1>Insights</h1>
          <p>
            {isOwner
              ? "A concise read on expiry pressure, capital at risk, and slow movers — built from the stock already on your desk."
              : "Understand where your list is heading before it becomes a write-off. Richer breakdowns and optional digest highlights are included on Owner edition; your current plan still has the full desk and expiry alerts."}
          </p>
        </div>

        <section id="inventory-workspace" className="ed-card-glass" style={{ borderRadius: 14, padding: "0.85rem 0.95rem", marginBottom: "0.8rem", scrollMarginTop: "5.5rem" }}>
          <h2 className="ed-dashboard-section-title" style={{ fontSize: "1.05rem", marginBottom: "0.35rem" }}>
            Inventory Workspace
          </h2>
          <p className="ed-inventory-sections__hint" style={{ marginBottom: "0.6rem" }}>
            Inventory sections and line visibility moved here to keep the main dashboard cleaner.
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "0.55rem",
              flexWrap: "wrap",
              gap: "0.5rem",
              position: "relative",
              zIndex: 2,
            }}
          >
            <button
              type="button"
              className="ed-btn-ghost ed-insights-section-toggle"
              onClick={() => setShowExtendedSections((v) => !v)}
              style={{ fontSize: "0.78rem", padding: "0.42rem 0.72rem", minHeight: "40px", fontWeight: 700 }}
            >
              {showExtendedSections ? "Hide Extended Sections" : "Show Extended Sections"}
            </button>
          </div>

          <div
            className="ed-inventory-sections__grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: "0.65rem",
              marginBottom: "0.7rem",
            }}
          >
            {INVENTORY_CATEGORY_IDS.map((cid) => {
              const meta = INVENTORY_CATEGORY_LABELS[cid];
              const agg = inventorySectionAgg[cid];
              const tierParts = [
                { key: "overdue", label: "Overdue", n: agg.overdue },
                { key: "critical", label: "Critical", n: agg.critical },
                { key: "warning", label: "At Risk", n: agg.warning },
                { key: "safe", label: "Safe", n: agg.safe },
              ];
              const isDropHover = dropHoverCategory === cid;
              return (
                <div
                  key={cid}
                  className="ed-inventory-section-card ed-card-glass"
                  onDragOver={(e) => onCategoryDragOver(e, cid)}
                  onDrop={(e) => onCategoryDrop(e, cid)}
                  style={{
                    outline: isDropHover ? "2px dashed var(--ed-ui-info-strong)" : undefined,
                    outlineOffset: 2,
                    transition: "outline-color 0.12s ease",
                  }}
                  role="group"
                  aria-label={`${meta.title} — drop here to classify`}
                >
                  <div className="ed-inventory-section-card__head">
                    <span className="ed-inventory-section-card__icon" aria-hidden>
                      {meta.icon}
                    </span>
                    <span className="ed-inventory-section-card__title">{meta.title}</span>
                  </div>
                  <div className="ed-inventory-section-card__count">{agg.total} items</div>
                  <div className="ed-inventory-section-card__tiers" role="list" aria-label={`Expiry mix for ${meta.title}`}>
                    {tierParts.map((t) => (
                      <span key={t.key} className={`ed-inventory-tier ed-inventory-tier--${t.key}`} role="listitem" title={t.label}>
                        <span className="ed-inventory-tier__n">{t.n}</span>
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    draggable={false}
                    className="ed-btn-ghost"
                    onClick={() => setExpandedCategory((c) => (c === cid ? null : cid))}
                    style={{
                      marginTop: "0.45rem",
                      width: "100%",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      padding: "0.38rem 0.45rem",
                      borderRadius: 10,
                    }}
                  >
                    {expandedCategory === cid ? "Hide line detail ▲" : "Line detail by risk ▼"}
                  </button>
                </div>
              );
            })}
          </div>

          {expandedCategory ? (
            <div
              className="ed-card-glass ed-fade-in"
              style={{
                marginBottom: "0.75rem",
                padding: "0.75rem 0.85rem",
                borderRadius: 14,
                border: "1px solid var(--ed-ui-info-border)",
                background: "linear-gradient(180deg, var(--ed-glass-bg) 0%, var(--ed-ui-info-bg) 100%)",
              }}
              role="region"
              aria-label={`${INVENTORY_CATEGORY_LABELS[expandedCategory].title} line detail`}
            >
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "0.5rem", marginBottom: "0.55rem" }}>
                <h3 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 800, color: "var(--ed-text)", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                  <span aria-hidden>{INVENTORY_CATEGORY_LABELS[expandedCategory].icon}</span>
                  {INVENTORY_CATEGORY_LABELS[expandedCategory].title}
                  <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--ed-text-secondary)" }}>· lines by expiry pressure</span>
                </h3>
                <button type="button" className="ed-btn-ghost" style={{ fontSize: "0.72rem", fontWeight: 700 }} onClick={() => setExpandedCategory(null)}>
                  Close
                </button>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: "0.55rem",
                }}
              >
                {(["overdue", "critical", "warning", "safe"] as const).map((tier) => {
                  const metaTier = TIER_META[tier];
                  const list = medsByCategoryTier[expandedCategory][tier];
                  return (
                    <div
                      key={tier}
                      style={{
                        borderRadius: 12,
                        border: `1px solid var(--ed-border)`,
                        borderLeftWidth: 4,
                        borderLeftColor: metaTier.border,
                        background: metaTier.tint,
                        padding: "0.5rem 0.55rem",
                        minHeight: 72,
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.35rem",
                      }}
                    >
                      <div style={{ fontSize: "0.68rem", fontWeight: 800, color: "var(--ed-text)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        {metaTier.label}
                        <span style={{ fontWeight: 700, color: "var(--ed-text-secondary)", marginLeft: "0.25rem" }}>({list.length})</span>
                      </div>
                      {list.length === 0 ? (
                        <p style={{ margin: 0, fontSize: "0.72rem", color: "var(--ed-text-muted)", fontStyle: "italic" }}>None in this band.</p>
                      ) : (
                        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.28rem", maxHeight: 220, overflowY: "auto" }}>
                          {list.map((m) => (
                            <li
                              key={String(m.id)}
                              style={{
                                fontSize: "0.74rem",
                                fontWeight: 600,
                                color: "var(--ed-text)",
                                lineHeight: 1.35,
                                padding: "0.28rem 0.35rem",
                                borderRadius: 8,
                                background: "var(--ed-glass-bg)",
                                border: "1px solid var(--ed-border)",
                              }}
                            >
                              <span className="ed-insights-line-name" style={{ fontSize: "0.74rem" }}>
                                {m.name || "Line"}
                                {m.dosage ? <span className="ed-insights-line-dose"> · {m.dosage}</span> : null}
                              </span>
                              <span style={{ display: "block", fontSize: "0.68rem", fontWeight: 500, color: "var(--ed-text-secondary)", marginTop: "0.12rem" }}>
                                {Number(m.quantity) || 0} {m.unit || "units"} · exp {String(m.expiryMonth).padStart(2, "0")}/{m.expiryYear}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          <p className="ed-inventory-sections__hint" style={{ margin: "0 0 0.45rem", fontSize: "0.76rem" }}>
            Unclassified lines only. <strong style={{ color: "var(--ed-text)" }}>Drag</strong> one or more cards into Fridge, Schedule 6, Schedule 3–5, or OTC 1–2.{" "}
            <strong style={{ color: "var(--ed-text)" }}>Ctrl</strong> or <strong style={{ color: "var(--ed-text)" }}>⌘</strong>+click to multi-select;{" "}
            <strong style={{ color: "var(--ed-text)" }}>Esc</strong> clears selection. You can also set category when you{" "}
            <Link href="/dashboard" style={{ color: "var(--ed-ui-info-strong)", fontWeight: 700 }}>
              edit on the desk
            </Link>
            .
          </p>
          {classifyError ? (
            <p style={{ margin: "0 0 0.5rem", fontSize: "0.78rem", color: "var(--ed-ui-critical)", fontWeight: 600 }} role="alert">
              {classifyError}
            </p>
          ) : null}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "0.65rem" }}>
            {unclassifiedMeds.length === 0 ? (
              <p style={{ gridColumn: "1 / -1", margin: 0, fontSize: "0.8rem", color: "var(--ed-text-secondary)" }}>
                Nothing waiting for a category — every line is classified, or your list is empty. Add stock on the desk or edit a line to change its shelf group.
              </p>
            ) : (
              unclassifiedMeds.map((m) => {
                const idStr = String(m.id);
                const selected = selectedUnclassifiedIds.has(idStr);
                return (
                  <div
                    key={idStr}
                    draggable={!classifyBusy}
                    onDragStart={(e) => onCardDragStart(e, idStr)}
                    onDragEnd={onCardDragEnd}
                    onClick={(e) => {
                      if (suppressCardClickRef.current) return;
                      toggleCardSelect(idStr, e.ctrlKey || e.metaKey);
                    }}
                    className="ed-card-glass"
                    style={{
                      borderRadius: 12,
                      padding: "0.62rem 0.68rem",
                      cursor: classifyBusy ? "wait" : "grab",
                      boxShadow: selected ? "0 0 0 2px var(--ed-ui-info-strong)" : undefined,
                      opacity: classifyBusy ? 0.72 : 1,
                    }}
                    aria-selected={selected}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleCardSelect(idStr, e.ctrlKey || e.metaKey);
                      }
                    }}
                  >
                    <div className="ed-insights-line-name">
                      {m.name || "Medication"}
                      {m.dosage ? <span className="ed-insights-line-dose"> · {m.dosage}</span> : null}
                    </div>
                    <p style={{ margin: "0.2rem 0 0", fontSize: "0.74rem", color: "var(--ed-text-secondary)" }}>
                      {Number(m.quantity) || 0} {m.unit || "units"} · {String(m.expiryMonth).padStart(2, "0")}/{m.expiryYear}
                    </p>
                  </div>
                );
              })
            )}
          </div>
        </section>

        <div id="inventory-health" style={{ scrollMarginTop: "5.5rem" }}>
          <DashboardInsightHub
            meds={meds}
            userId={userId}
            supabaseReady={Boolean(supabase)}
            supabaseClient={supabase}
            runtimePlan={runtimePlan}
            isFree={runtimePlan === PLANS.FREE}
          />
        </div>

        {loading && !showExtendedSections ? (
          <p style={{ color: "var(--ed-text-secondary)", fontSize: "0.95rem", marginTop: "0.5rem" }}>Loading your plan…</p>
        ) : null}

        {showExtendedSections ? (
          <div id="insights-extended" style={{ scrollMarginTop: "5.5rem" }}>
            {loading ? (
              <p style={{ color: "var(--ed-text-secondary)", fontSize: "0.95rem", padding: "0.35rem 0 0.75rem" }}>Loading extended sections…</p>
            ) : (
              <>
                {!isOwner ? (
                  <section className="ed-insights-upgrade" aria-labelledby="insights-upgrade-heading">
                    <h2 id="insights-upgrade-heading">Go deeper with Owner edition</h2>
                    <p>
                      Owner unlocks expanded trend views here in the app, plus up to <strong>three short insight lines</strong> in your twice-weekly email when your data supports
                      them. Manager edition keeps the same capture workflow and operational emails without that extra layer — so we can price each tier sustainably.
                    </p>
                    <p style={{ margin: "0 0 1rem", fontSize: "0.9rem", lineHeight: 1.6, color: "var(--ed-text-secondary)" }}>
                      List price for Owner is <strong>{formatZarAmount(OWNER_MONTHLY_ZAR)}</strong> / month before discounts. Final amounts and yearly options are always shown on
                      checkout.
                    </p>
                    <Link href="/manage-subscription" className="ed-btn-primary" style={{ display: "inline-block", textDecoration: "none", padding: "0.6rem 1.2rem", fontWeight: 700, fontSize: "0.9rem", borderRadius: 12 }}>
                      View plans &amp; upgrade
                    </Link>
                  </section>
                ) : null}
                {!canUse("exportReportsEnabled") ? (
                  <UpgradePrompt
                    feature="insightsOwner"
                    triggerLabel="Advanced Insights"
                    benefitStatement="Unlock deeper expiry intelligence and export-ready operational summaries for management reviews."
                    requiredPlan={PLANS.TEAM}
                    onDismiss={() => {}}
                    variant="inline"
                  />
                ) : null}

                <section
                  className={`ed-insights-preview ${!isOwner ? "ed-insights-preview--locked" : ""}`}
                  aria-labelledby="insights-preview-heading"
                >
                  <h2 id="insights-preview-heading">{isOwner ? "Your snapshot" : "Preview — included on Owner"}</h2>
                  <p>
                    {isOwner
                      ? "These panels summarise patterns from your current list. They refresh when your stock or dates change — not on a fixed “many times per day” schedule, and not on hover."
                      : "On Owner, this area fills with live summaries: how expiry is distributed, where money sits in risk bands, and which lines are barely moving. Upgrade to connect it to your inventory."}
                  </p>
                  <div className="ed-insights-tile-grid">
                    {[
                      { label: "Expiry curve", hint: "Near-term vs later dates" },
                      { label: "Capital at risk", hint: "Value in the danger window" },
                      { label: "Slow movers", hint: "Low turnover candidates" },
                    ].map(({ label, hint }) => (
                      <div key={label} className="ed-insights-tile">
                        <span style={{ fontSize: "0.65rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--ed-text-secondary)", marginBottom: "0.35rem" }}>
                          {hint}
                        </span>
                        {label}
                      </div>
                    ))}
                  </div>
                  <p className="ed-insights-footnote">
                    {isOwner
                      ? "Email digest: Owner accounts may include up to three optional insight snippets when your data supports them. You can adjust email preferences in Settings on the desk."
                      : "Twice-weekly emails on all paid tiers include operational reminders. The extra digest snippets above are an Owner benefit."}
                  </p>
                </section>

                <section className="ed-insights-letter" aria-labelledby="insights-letter-heading">
                  <h2 id="insights-letter-heading">Optional: practice letter</h2>
                  <p>
                    Short, practical notes on ordering discipline, safe destruction, and shelf hygiene. If you opt in to marketing mail, we may mention relevant ExpiryDesk features — you
                    can unsubscribe from those anytime; service emails about your account stay separate.
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", alignItems: "center" }}>
                    <input
                      type="email"
                      value={emailAddr}
                      onChange={(e) => setEmailAddr(e.target.value)}
                      placeholder="you@pharmacy.co.za"
                      style={{
                        flex: "1 1 220px",
                        padding: "0.6rem 0.85rem",
                        borderRadius: 10,
                        border: "1px solid var(--ed-border)",
                        fontSize: "0.9rem",
                      }}
                    />
                    <button type="button" className="ed-btn-primary" disabled={subLoading} onClick={() => void subscribeNewsletter()} style={{ padding: "0.6rem 1rem", fontWeight: 700 }}>
                      {subLoading ? "Saving…" : subscribed ? "Preferences saved" : "Register for updates"}
                    </button>
                  </div>
                  {subError ? <p style={{ margin: "0.65rem 0 0", color: "var(--ed-ui-critical)", fontSize: "0.85rem" }}>{subError}</p> : null}
                  {subscribed && !subError ? (
                    <p style={{ margin: "0.65rem 0 0", color: "var(--ed-ui-safe)", fontSize: "0.85rem", fontWeight: 600 }}>You’re on the list — we’ll use this address for the letter.</p>
                  ) : null}
                </section>
              </>
            )}
          </div>
        ) : null}

        {classifyUndo ? (
          <div
            className="ed-card-glass"
            style={{
              position: "fixed",
              left: "50%",
              bottom: "max(1rem, env(safe-area-inset-bottom, 0px))",
              transform: "translateX(-50%)",
              zIndex: 1080,
              maxWidth: "min(420px, calc(100vw - 2rem - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px)))",
              width: "min(420px, calc(100vw - 2rem - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px)))",
              padding: "0.65rem 0.85rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "0.75rem",
              borderRadius: 14,
              border: "1px solid var(--ed-border)",
              background: "var(--ed-glass-bg)",
              boxShadow: "0 12px 40px rgba(15, 23, 42, 0.12)",
            }}
            role="status"
            aria-live="polite"
          >
            <span style={{ fontSize: "0.8125rem", color: "var(--ed-text)", lineHeight: 1.45 }}>
              <strong>{classifyUndo.ids.length === 1 ? "1 line" : `${classifyUndo.ids.length} lines`}</strong>
              <span style={{ color: "var(--ed-text-secondary)" }}> moved to {classifyUndo.categoryTitle}.</span>
            </span>
            <button
              type="button"
              className="ed-btn-secondary"
              onClick={() => void undoClassify()}
              style={{ flexShrink: 0, padding: "0.4rem 0.85rem", fontSize: "0.8125rem", fontWeight: 600 }}
            >
              Undo
            </button>
          </div>
        ) : null}
      </main>
    </div>
  );
}
